import semver from 'semver';

import { getOpenReviewVersion } from 'shared/app-versions';

// Swell staff sign in through the admin client
const SUPER_ADMIN_CLIENT_ID = 'admin';

/**
 * Groups the flat comment page into review threads scoped by app version.
 * Threads of the version whose review is open stay writable; every earlier
 * review is closed, so its threads are read-only history.
 *
 * @param {object} app
 * @returns {{
 *   openVersion: string|undefined,
 *   active: Array<object>,
 *   previous: Array<{ version: string, threads: Array<object> }>,
 *   closedCount: number,
 * }}
 */
export function getReviewThreads(app) {
  const openVersion = getOpenReviewVersion(app);
  const threads = toThreads(app.review_comments?.results || []);
  const active = openVersion
    ? threads.filter(({ version }) => version === openVersion)
    : [];
  const closed = openVersion
    ? threads.filter(({ version }) => version !== openVersion)
    : threads;

  return {
    openVersion,
    active,
    previous: groupByVersion(closed),
    closedCount: closed.length,
  };
}

function toThreads(comments) {
  const chronological = [...comments].sort(compareDate);
  const threads = chronological
    .filter(({ top_id: topId }) => !topId)
    .map((comment) => ({ ...comment, replies: [] }));

  for (const comment of chronological) {
    const thread =
      comment.top_id && threads.find(({ id }) => id === comment.top_id);

    if (thread) {
      thread.replies.push(comment);
    }
  }

  return threads;
}

function groupByVersion(threads) {
  const groups = [];

  for (const thread of threads) {
    const group = groups.find(({ version }) => version === thread.version);

    if (group) {
      group.threads.push(thread);
    } else {
      groups.push({ version: thread.version, threads: [thread] });
    }
  }

  return groups.sort(compareVersionDesc);
}

function compareDate(commentA, commentB) {
  return new Date(commentA.date_created) - new Date(commentB.date_created);
}

function compareVersionDesc(groupA, groupB) {
  return semver.valid(groupA.version) && semver.valid(groupB.version)
    ? semver.rcompare(groupA.version, groupB.version)
    : 0;
}

/**
 * The plain-text opening of a comment, for the one-line summary of a closed
 * thread. Comment bodies are editor HTML.
 *
 * @param {string} html
 * @returns {string}
 */
export function getCommentPreview(html) {
  // A parsed document runs no handlers, as an element of the page would
  const { body } = new DOMParser().parseFromString(html || '', 'text/html');

  return body.textContent.trim();
}

/**
 * The name a comment is signed with.
 *
 * @param {object} [user]
 * @returns {string}
 */
export function getCommentAuthorName(user) {
  return user?.name || user?.email || user?.username || '';
}

/**
 * Whether a comment's author writes on behalf of Swell. The portal API resolves
 * this into a flag because it never serves `client_id`; the admin reads the
 * expanded user record as it is stored.
 *
 * @param {object} [user]
 * @returns {boolean}
 */
export function isSuperAdminAuthor(user) {
  return (
    Boolean(user?.is_super_admin) || user?.client_id === SUPER_ADMIN_CLIENT_ID
  );
}

/**
 * The comment the partner is asked to act on: the newest thing a Swell reviewer
 * said out loud in the open review. Internal notes and the partner's own
 * replies are not answers to the partner, so they are skipped.
 *
 * @param {object} app
 * @returns {string|undefined}
 */
export function getLatestReviewerComment(app) {
  const { active } = getReviewThreads(app);
  const comments = active.flatMap((thread) => [thread, ...thread.replies]);
  const latest = comments
    .filter((comment) => !comment.internal && isSuperAdminAuthor(comment.user))
    .sort(compareDate)
    .pop();

  return latest?.comment;
}
